import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ALERT_STATUSES,
  type AlertStatus,
  SEARCH_ITEM_TYPES,
  type SearchItem,
  SearchResponseSchema,
} from '@vision360/contracts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useDashboards } from '@/features/dashboards/api';
import { useApi } from '@/lib/auth/session-context';
import { useSources } from '@/widgets/_sdk/hooks';

/**
 * Palette results (PRD VS-070, TechSpec §6.1). Local items (pages, cached
 * dashboards and sources) are matched synchronously so the first results
 * never wait on the network; `GET /search` is merged in when it answers.
 */

export type PaletteItemType = SearchItem['type'] | 'page';
export interface PaletteItem extends Omit<SearchItem, 'type'> {
  type: PaletteItemType;
}
export type PaletteGroup = PaletteItemType | 'recent';
export interface PaletteSection {
  group: PaletteGroup;
  items: PaletteItem[];
}

/** Ties break in this order (the API uses the same one for its types). */
export const GROUP_ORDER: readonly PaletteGroup[] = [
  'recent',
  'page',
  'dashboard',
  'source',
  'alert',
  'rule',
  'event',
];

export const PAGES = [
  { id: 'home', href: '/' },
  { id: 'sources', href: '/sources' },
  { id: 'alerts', href: '/alerts' },
  { id: 'rules', href: '/rules' },
  { id: 'settings', href: '/settings' },
] as const;

export const RECENT_KEY = 'v360.palette.recent';
const RECENT_MAX = 20;
const RECENT_SHOWN = 8;
/** A recent pick ranks above an equal match. */
export const RECENT_BOOST = 5;
const DEBOUNCE_MS = 150;
const REMOTE_LIMIT = 20;

const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().trim();

/**
 * Match level on the API's scale (score = level × 10): 3 exact, 2 prefix,
 * 1 substring, 0.5 subsequence ("tdb" finds "tableau de bord"), null none.
 * Case and accents are ignored.
 */
export function matchLevel(query: string, text: string): number | null {
  const q = fold(query);
  const t = fold(text);
  if (q === '') return null;
  if (t === q) return 3;
  if (t.startsWith(q)) return 2;
  if (t.includes(q)) return 1;
  let i = 0;
  for (const char of t) if (char === q[i]) i++;
  return i === q.length ? 0.5 : null;
}

export function matchLocal(query: string, candidates: readonly PaletteItem[]): PaletteItem[] {
  return candidates.flatMap((item) => {
    const level = matchLevel(query, item.title);
    return level === null ? [] : [{ ...item, score: level * 10 }];
  });
}

const isAlertStatus = (value: string | null): value is AlertStatus =>
  (ALERT_STATUSES as readonly (string | null)[]).includes(value);

/**
 * The API sends raw subtitles: a dashboard's mode, an alert's status, an
 * event's kind, a source's connector. Only mode-web dashboards open at /d/<id>,
 * so the others are dropped; alert statuses are put in words.
 */
export function fromRemote(
  items: readonly SearchItem[],
  alertStatus: (status: AlertStatus) => string,
): PaletteItem[] {
  return items.flatMap((item): PaletteItem[] => {
    if (item.type === 'dashboard') {
      return item.subtitle === 'web' ? [{ ...item, subtitle: null }] : [];
    }
    if (item.type === 'alert' && isAlertStatus(item.subtitle)) {
      return [{ ...item, subtitle: alertStatus(item.subtitle) }];
    }
    return [item];
  });
}

const keyOf = (item: { type: string; id: string }) => `${item.type}:${item.id}`;
const groupRank = (group: PaletteGroup) => GROUP_ORDER.indexOf(group);

/** Remote wins over local for the same (type, id); recent picks get a boost; best first. */
export function mergeResults(
  local: readonly PaletteItem[],
  remote: readonly PaletteItem[],
  recents: readonly RecentPick[],
): PaletteItem[] {
  const byKey = new Map<string, PaletteItem>();
  for (const item of [...local, ...remote]) byKey.set(keyOf(item), item);
  const recent = new Set(recents.map(keyOf));
  return [...byKey.values()]
    .map((item) => (recent.has(keyOf(item)) ? { ...item, score: item.score + RECENT_BOOST } : item))
    .sort((a, b) => b.score - a.score || groupRank(a.type) - groupRank(b.type));
}

/** Sections in the order of their best item, so the top result is always first. */
export function groupItems(items: readonly PaletteItem[]): PaletteSection[] {
  const sections = new Map<PaletteGroup, PaletteItem[]>();
  for (const item of items) {
    const list = sections.get(item.type);
    if (list) list.push(item);
    else sections.set(item.type, [item]);
  }
  return [...sections].map(([group, list]) => ({ group, items: list }));
}

// ── Recent picks (per viewer, browser storage; losing them costs nothing) ──

const RecentPickSchema = z.object({
  type: z.enum([...SEARCH_ITEM_TYPES, 'page']),
  id: z.string(),
  title: z.string(),
  // Only same-origin SPA paths: this value is navigated to.
  href: z.string().refine((h) => h.startsWith('/') && !h.startsWith('//')),
  at: z.string(),
});
export type RecentPick = z.infer<typeof RecentPickSchema>;

export function readRecents(): RecentPick[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      const pick = RecentPickSchema.safeParse(entry);
      return pick.success ? [pick.data] : [];
    });
  } catch {
    return [];
  }
}

export function recordRecent(item: PaletteItem, now = new Date()): RecentPick[] {
  const pick: RecentPick = {
    type: item.type,
    id: item.id,
    title: item.title,
    href: item.href,
    at: now.toISOString(),
  };
  const next = [pick, ...readRecents().filter((r) => keyOf(r) !== keyOf(pick))].slice(
    0,
    RECENT_MAX,
  );
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // Storage blocked or full: the pick still works, it is just not remembered.
  }
  return next;
}

const fromRecent = (r: RecentPick): PaletteItem => ({
  type: r.type,
  id: r.id,
  title: r.title,
  subtitle: null,
  href: r.href,
  score: 0,
  at: null,
});

/** Empty query: the last picks, then every page. */
export function idleSections(
  recents: readonly RecentPick[],
  pages: readonly PaletteItem[],
): PaletteSection[] {
  const sections: PaletteSection[] = [];
  if (recents.length > 0) {
    sections.push({ group: 'recent', items: recents.slice(0, RECENT_SHOWN).map(fromRecent) });
  }
  sections.push({ group: 'page', items: [...pages] });
  return sections;
}

function useDebounced(value: string, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function usePaletteSearch(query: string) {
  const { t } = useTranslation(['palette', 'alerts']);
  const api = useApi();
  const dashboards = useDashboards();
  const sources = useSources();
  const [recents, setRecents] = useState(readRecents);
  const q = query.trim();
  const debounced = useDebounced(q, DEBOUNCE_MS);

  const remote = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) =>
      api.request(`/api/v1/search?q=${encodeURIComponent(debounced)}&limit=${REMOTE_LIMIT}`, {
        schema: SearchResponseSchema,
        signal,
      }),
    enabled: debounced !== '',
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });

  const pages = useMemo(
    () =>
      PAGES.map(
        (page): PaletteItem => ({
          type: 'page',
          id: page.id,
          title: t(`palette:pages.${page.id}`),
          subtitle: null,
          href: page.href,
          score: 0,
          at: null,
        }),
      ),
    [t],
  );

  const candidates = useMemo(
    (): PaletteItem[] => [
      ...pages,
      ...(dashboards.data?.dashboards ?? []).map(
        (d): PaletteItem => ({
          type: 'dashboard',
          id: d.id,
          title: d.name,
          subtitle: null,
          href: `/d/${d.id}`,
          score: 0,
          at: null,
        }),
      ),
      ...(sources.data?.sources ?? []).map(
        (s): PaletteItem => ({
          type: 'source',
          id: s.id,
          title: s.name,
          subtitle: s.connectorId,
          href: '/sources',
          score: 0,
          at: null,
        }),
      ),
    ],
    [pages, dashboards.data, sources.data],
  );

  const remoteItems =
    q !== '' ? fromRemote(remote.data?.items ?? [], (status) => t(`alerts:status.${status}`)) : [];
  const sections =
    q === ''
      ? idleSections(recents, pages)
      : groupItems(mergeResults(matchLocal(q, candidates), remoteItems, recents));

  const record = useCallback((item: PaletteItem) => setRecents(recordRecent(item)), []);

  return {
    sections,
    pending: q !== '' && (debounced !== q || remote.isFetching),
    error: q !== '' && remote.isError,
    record,
  };
}
